import { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCustomSection,
  getDefaultSection,
} from 'legacyfrontend/redux/actions/custom-section-actions';
import { getSectionDefaultById } from 'legacyfrontend/api/services/endpointSection';
import {
  selectTemplateData,
  selectToken,
} from 'legacyfrontend/redux/selectors/application-selectors';

const MAIN_MENU_SECTION_TYPES = ['HTML', 'BANNER', 'URL'];

export const useCustomSection = (id) => {
  const dispatch = useDispatch();
  const templateData = useSelector(selectTemplateData);
  const token = useSelector(selectToken);
  const [fetchedSection, setFetchedSection] = useState(null);
  const [fetchedSectionId, setFetchedSectionId] = useState(null);
  const [htmlDefault, setHtmlDefault] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const mainMenuSection = useMemo(() => {
    return (templateData?.mainMenu || []).find((item) => String(item.id) === String(id));
  }, [templateData?.mainMenu, id]);

  const mainMenuSectionType = mainMenuSection?.tipoSeccion?.nombre?.toUpperCase();
  const shouldUseMainMenuSection = Boolean(
    mainMenuSection && MAIN_MENU_SECTION_TYPES.includes(mainMenuSectionType)
  );
  const shouldUseFetchedSection = String(fetchedSectionId) === String(id);
  const sectionRaw =
    (shouldUseMainMenuSection
      ? mainMenuSection
      : shouldUseFetchedSection
        ? fetchedSection
        : null) || {};
  const sectionHasRawData = Object.keys(sectionRaw).length > 0;
  const sectionContent = typeof sectionRaw.contenido === 'string' ? sectionRaw.contenido : '';

  const fetchDefaultSection = useCallback(async () => {
    if (!id || !token) {
      return;
    }

    return getSectionDefaultById(id, token);
  }, [id, token]);

  useEffect(() => {
    if (!id) {
      return;
    }

    if (shouldUseMainMenuSection) {
      setFetchedSection(null);
      setFetchedSectionId(null);
      setHtmlDefault();
      setIsLoading(false);
      setError(null);
      return;
    }

    let isActive = true;

    const fetchSection = async () => {
      setFetchedSection(null);
      setFetchedSectionId(null);
      setHtmlDefault();
      setIsLoading(true);
      setError(null);

      try {
        const section = await dispatch(getCustomSection(id));
        if (isActive) {
          setFetchedSection(section?.payload || section);
          setFetchedSectionId(id);
        }
      } catch {
        try {
          const defaultSection = await dispatch(getDefaultSection(id));
          if (isActive) {
            setFetchedSection(defaultSection?.payload || defaultSection);
            setFetchedSectionId(id);
          }
        } catch {
          try {
            const response = await fetchDefaultSection();
            if (isActive) {
              setHtmlDefault(response.data);
              setFetchedSectionId(id);
            }
          } catch (directDefaultError) {
            if (isActive) {
              setError(directDefaultError);
            }
          }
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchSection();

    return () => {
      isActive = false;
    };
  }, [id, dispatch, shouldUseMainMenuSection, fetchDefaultSection]);

  const isDirectDefaultUrl = !sectionHasRawData && Boolean(htmlDefault?.url);
  const isHtmlContent = !isDirectDefaultUrl && sectionRaw.tipoSeccion?.nombre !== 'URL';

  const hasValidContent = useCallback(() => {
    if (isHtmlContent) {
      return sectionContent.trim() !== '';
    } else {
      return sectionContent || htmlDefault?.url;
    }
  }, [isHtmlContent, sectionContent, htmlDefault?.url]);

  const hasContent = hasValidContent();
  const sectionExists = sectionHasRawData || Boolean(htmlDefault?.url);
  const contentUrl = sectionContent || htmlDefault?.url;

  const sectionTitle =
    sectionRaw.descripcion || sectionRaw.nombre || mainMenuSection?.descripcion || '';
  const loadedSectionId = shouldUseMainMenuSection ? id : fetchedSectionId;

  return {
    raw: sectionRaw,
    htmlDefault,
    isHtmlContent,
    hasContent,
    sectionExists,
    sectionTitle,
    loadedSectionId,
    contentUrl,
    isLoading,
    error,
  };
};
